import { useState } from "react";
import { INPUT_TYPE_LABELS, hasContent, summarizeEntry, fmtDate, fmtSeconds } from "@/villa142/config";
import { COMPOSERS } from "@/villa142/inputs";
import { VoiceNotes } from "@/villa142/recorder";
import { UploadTray } from "@/villa142/uploads";
import { mediaUrl, studyUrl } from "@/villa142/api";
import { AudioPlayer } from "@/villa142/player";
import { ConfirmModal } from "@/villa142/admin/ConfirmModal";

export const ContributionDisplay = ({ question, entry, token, people, onEdit, onDelete, audioExtras, imageExtras, children }) => {
  const lines = summarizeEntry(question, entry);
  const audios = (entry.media || []).filter((m) => m.kind === "audio");
  const images = (entry.media || []).filter((m) => m.kind === "image");
  const typeBits = ((entry.input_type || question?.input) === "voice_only")
    ? []
    : [INPUT_TYPE_LABELS[entry.input_type || question?.input] || "Written"];
  if (audios.length) typeBits.push(audios.length === 1 ? "1 voice note" : `${audios.length} voice notes`);
  if (images.length) typeBits.push(images.length === 1 ? "1 image" : `${images.length} images`);
  return (
    <div className="v142-entry-block" data-testid={`v142-entry-${entry.key}-${entry.contributor}`}>
      <div className="v142-entry-head">
        <span className="v142-entry-group">{people ? people.entryLabel(entry.contributor) : entry.contributor}</span>
        <span className="v142-entry-meta">{typeBits.join(" · ")}{entry.updated_at ? ` · ${fmtDate(entry.updated_at)}` : ""}</span>
        {onEdit && (
          <button type="button" className="v142-link v142-entry-edit" data-testid={`v142-edit-${entry.key}-${entry.contributor}`} onClick={onEdit}>
            Edit
          </button>
        )}
        {onDelete && (
          <button type="button" className="v142-link v142-danger-link" data-testid={`v142-delete-${entry.key}-${entry.contributor}`} onClick={onDelete}>
            Delete this reflection
          </button>
        )}
      </div>
      {lines.map((line, i) => (
        <p className="v142-entry-text" key={i}>{line}</p>
      ))}
      {audios.map((m, i) => (
        <div className="v142-entry-audio" key={m.id}>
          <span className="v142-entry-meta">Voice note {i + 1}{m.duration != null ? ` · ${fmtSeconds(m.duration)}` : ""}</span>
          <AudioPlayer src={mediaUrl(m.id, token)} duration={m.duration} label={`voice note ${i + 1}`} testid={`v142-display-audio-${entry.key}-${i + 1}`} />
          {audioExtras && audioExtras(m, i)}
        </div>
      ))}
      {images.length > 0 && (
        <div className="v142-entry-imgs">
          {images.map((m) => (
            <figure key={m.id} className="v142-entry-fig">
              <img
                src={mediaUrl(m.id, token, "display")}
                srcSet={`${mediaUrl(m.id, token, "thumb")} 600w, ${mediaUrl(m.id, token, "display")} 1600w`}
                sizes="(max-width: 640px) 45vw, 240px"
                alt={m.caption || "Shared reference"}
                className="v142-entry-img"
                loading="lazy"
              />
              {m.caption && <figcaption className="v142-entry-capt">{m.caption}</figcaption>}
              {imageExtras && imageExtras(m)}
            </figure>
          ))}
        </div>
      )}
      {children}
    </div>
  );
};

const joinNames = (names) =>
  names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}` : names[0] || "";

const othersLine = (others, people) => {
  const persons = others.filter((c) => c !== people.sharedId).map((c) => people.name(c));
  const shared = others.includes(people.sharedId);
  if (!persons.length) return shared ? "A shared reflection has already been added." : "";
  const base = `${joinNames(persons)} ${persons.length > 1 ? "have" : "has"} already added ${persons.length > 1 ? "reflections" : "a reflection"}`;
  return shared ? `${base}, and there is a shared reflection too.` : `${base}.`;
};

const ConflictNotice = ({ question, conflict, onResolve }) => {
  const latest = conflict.latest;
  const lines = latest ? summarizeEntry(question, latest) : [];
  return (
    <div className="v142-conflict" role="alert" data-testid={`v142-conflict-${question.id}`}>
      <p className="v142-conflict-title">This reflection was updated elsewhere. Review the latest version before saving your changes.</p>
      {latest && (
        <div className="v142-conflict-latest" data-testid={`v142-conflict-latest-${question.id}`}>
          <span className="v142-mini-label">
            Latest version{latest.updated_at ? ` · ${fmtDate(latest.updated_at)}` : ""}
          </span>
          {lines.length ? lines.map((line, i) => <p className="v142-entry-text" key={i}>{line}</p>) : (
            <p className="v142-entry-text v142-conflict-empty">No written reflection in the latest version.</p>
          )}
        </div>
      )}
      <p className="v142-conflict-hint">Your unsaved changes are still here below, exactly as you left them.</p>
      <div className="v142-conflict-actions">
        <button type="button" className="v142-ghost v142-conflict-btn" data-testid={`v142-conflict-use-latest-${question.id}`} onClick={() => onResolve("latest")}>
          Use the latest version
        </button>
        <button type="button" className="v142-btn v142-conflict-btn" data-testid={`v142-conflict-keep-mine-${question.id}`} onClick={() => onResolve("mine")}>
          Keep my changes
        </button>
      </div>
    </div>
  );
};

export const QuestionCard = ({
  question, entriesMap, active, responder, people, isFirst, conflict, onResolveConflict, onSetActive, onValue, onDeleteReflection, mediaOps,
}) => {
  const byC = entriesMap[question.id] || {};
  const withContent = people.order.filter((c) => hasContent(byC[c]));
  const entry = byC[active] || { key: question.id, contributor: active, value: {}, media: [] };
  const value = entry.value || {};
  const Composer = COMPOSERS[question.input] || COMPOSERS.long_text;
  const cfg = question.config || {};
  const study = question.input === "visual_study" ? cfg.study : null;
  const [confirmDel, setConfirmDel] = useState(null);
  const [perspOpen, setPerspOpen] = useState(false);

  const trayCfg = question.uploads
    ? { key: question.id, max: question.uploads.max, label: null, captionPrompt: question.uploads.captionPrompt || "A few words, if you like" }
    : null;

  // Slots shown as tabs: saved/draft reflections, the open slot, and the global responder's slot (even when empty).
  // The global responder always comes first so their slot is one tap away.
  const slotOrder = responder ? [responder, ...people.order.filter((c) => c !== responder)] : people.order;
  const tabIds = slotOrder.filter((c) => withContent.includes(c) || c === active || c === responder);
  const showTabs = tabIds.length >= 2;
  const missing = people.order.filter((c) => !hasContent(byC[c]) && c !== active && c !== responder);
  const isShared = active === people.sharedId;
  const activeEmpty = !hasContent(byC[active]);
  const isOverrideDraft = Boolean(responder) && active !== responder && activeEmpty;
  const others = withContent.filter((c) => c !== active);
  const isVoiceOnly = question.input === "voice_only";
  const voiceMax = Math.min(cfg.voiceMax || 3, 3);
  const voiceSeconds = Math.min(cfg.voiceSeconds || 180, 180);
  const voiceOwnerLabel = (c) => (c === people.sharedId ? "Shared voice notes" : `${people.name(c)}'s voice notes`);
  const tabLabel = (c) => (isVoiceOnly ? (c === people.sharedId ? "Shared notes" : `${people.name(c)}'s notes`) : people.entryLabel(c));
  const alreadyLine = !isVoiceOnly && activeEmpty && others.length ? othersLine(others, people) : "";

  const choiceLabel = (c) =>
    isVoiceOnly
      ? (c === people.sharedId ? "Add shared voice notes" : `Add ${people.name(c)}'s voice notes`)
      : (c === people.sharedId ? "Add a shared reflection" : `Add ${people.name(c)}'s reflection`);

  const askDelete = (c) =>
    setConfirmDel({
      contributor: c,
      title: "Delete this reflection",
      body: `This removes ${people.entryLabel(c).toLowerCase()} on this question — the typed text, voice notes, uploads and structured entries attached to it.`,
    });

  return (
    <section className="v142-screen v142-qcard" data-testid={`v142-question-${question.id}`}>
      {study && (
        <figure className="v142-visual-bleed">
          {study.has_image && <img src={studyUrl(study.id)} alt={study.title} className="v142-visual-img" />}
          {(study.title || study.caption) && (
            <figcaption className="v142-visual-caption">
              <span className="v142-visual-title">{study.title}</span>
              {study.caption && <span className="v142-visual-sub">{study.caption}</span>}
            </figcaption>
          )}
        </figure>
      )}

      <div className="v142-field-head">
        <h2 className="v142-label-main v142-flow-heading" tabIndex={-1} data-testid={`v142-question-heading-${question.id}`}>{study ? "Your reaction" : question.label}</h2>
        {question.helper && <p className="v142-support">{question.helper}</p>}
      </div>

      {showTabs && (
        <div className="v142-owner-tabs" role="tablist" aria-label="Reflections on this question" data-testid={`v142-owner-tabs-${question.id}`}>
          {tabIds.map((c) => (
            <button
              key={c}
              type="button"
              role="tab"
              aria-selected={active === c}
              className={`v142-owner-tab${active === c ? " is-active" : ""}`}
              data-testid={`v142-perspective-${question.id}-${c}`}
              onClick={() => onSetActive(question.id, c)}
            >
              {tabLabel(c)}
            </button>
          ))}
        </div>
      )}

      <div className="v142-owner-head" data-testid={`v142-owner-head-${question.id}`}>
        {isOverrideDraft ? (
          <>
            <span className="v142-owner-name" data-testid={`v142-answering-as-${question.id}`}>
              {isVoiceOnly
                ? (isShared ? "Adding shared voice notes" : `Adding ${people.name(active)}'s voice notes`)
                : (isShared ? "Adding a shared reflection" : `Adding ${people.name(active)}'s reflection`)}
            </span>
            <p className="v142-owner-note" data-testid={`v142-override-note-${question.id}`}>
              {responder === people.sharedId ? "You are still responding together" : `${people.name(responder)} is still responding`}. This applies only to this question.
            </p>
          </>
        ) : (
          <>
            <span className="v142-owner-name" data-testid={`v142-answering-as-${question.id}`}>{isVoiceOnly ? voiceOwnerLabel(active) : people.entryLabel(active)}</span>
            {alreadyLine && (
              <p className="v142-owner-sub" data-testid={`v142-others-note-${question.id}`}>{alreadyLine}</p>
            )}
            {activeEmpty && !isVoiceOnly && (
              isFirst ? (
                <>
                  <p className="v142-owner-sub">Add each person who will live here.</p>
                  <p className="v142-owner-hint">Include their relationship, age, and any expected changes.</p>
                </>
              ) : (
                !alreadyLine && <p className="v142-owner-sub">{question.voice ? "Write a reflection, add a voice note, or do both." : "Add your thoughts below."}</p>
              )
            )}
          </>
        )}
      </div>

      {conflict && (
        <ConflictNotice
          question={question}
          conflict={conflict}
          onResolve={(choice) => onResolveConflict(question.id, active, choice)}
        />
      )}

      <div className="v142-composer" data-testid={`v142-composer-${question.id}`}>
        {trayCfg && cfg.uploadsFirst && (
          <UploadTray cfg={trayCfg} contributor={active} media={entry.media} ops={mediaOps} />
        )}
        {question.voice && !study && (question.input === "long_text" || question.input === "short_text") && (
          <span className="v142-mini-label v142-type-head">Type your reflection</span>
        )}
        {!isVoiceOnly && (
          <Composer
            question={question}
            value={value}
            onValue={(v) => onValue(question, active, v)}
            contributor={active}
            media={entry.media}
            ops={mediaOps}
            addLabel={isFirst ? "a household member" : null}
          />
        )}
        {question.voice && (
          <VoiceNotes
            entryKey={question.id}
            contributor={active}
            media={entry.media}
            ops={mediaOps}
            maxNotes={voiceMax}
            maxSeconds={voiceSeconds}
            deniedText={isVoiceOnly ? "No problem — you can leave this one open for now." : undefined}
          />
        )}
        {trayCfg && !cfg.uploadsFirst && (
          <UploadTray cfg={trayCfg} contributor={active} media={entry.media} ops={mediaOps} />
        )}
        {!isVoiceOnly && hasContent(byC[active]) && (
          <button
            type="button"
            className="v142-link v142-danger-link v142-self-delete"
            data-testid={`v142-delete-active-${question.id}`}
            onClick={() => askDelete(active)}
          >
            Delete this reflection
          </button>
        )}
      </div>

      {missing.length > 0 && (
        <div className="v142-add-perspective" data-testid={`v142-add-perspective-${question.id}`}>
          {!perspOpen ? (
            <button
              type="button"
              className="v142-perspective-toggle"
              data-testid={`v142-add-perspective-toggle-${question.id}`}
              aria-expanded={false}
              onClick={() => setPerspOpen(true)}
            >
              + Add another perspective
            </button>
          ) : (
            <div className="v142-perspective-choices" role="group" aria-label="Add another perspective">
              {missing.map((c) => (
                <button
                  key={c}
                  type="button"
                  className="v142-perspective-choice"
                  data-testid={`v142-add-perspective-${question.id}-${c}`}
                  onClick={() => { onSetActive(question.id, c); setPerspOpen(false); }}
                >
                  {choiceLabel(c)}
                </button>
              ))}
              <button
                type="button"
                className="v142-perspective-cancel"
                data-testid={`v142-add-perspective-cancel-${question.id}`}
                onClick={() => setPerspOpen(false)}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        open={Boolean(confirmDel)}
        title={confirmDel?.title}
        body={confirmDel?.body}
        note="This cannot be undone."
        confirmLabel="Delete"
        onCancel={() => setConfirmDel(null)}
        onConfirm={() => {
          onDeleteReflection(question.id, confirmDel.contributor);
          setConfirmDel(null);
        }}
      />
    </section>
  );
};
